import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import styled from '@emotion/styled';
import { TextFieldWithController } from '../TextField';
import { SelectWithController } from '../SelectMenus';
import BasicSignupFields from './BasicSignupFields';
import Button from '../Button';
import ValidationError from '../ValidationError';
import SpacingContainer from '../SpacingContainer';
import {
  getRequiredValidation,
  getUrlValidation,
} from '../lib/validationRules';

const CaptchaContainer = styled.div`
  .form-item {
    height: 102px
  }
`;

const SignupForm = ({
  firstNameLabel,
  lastNameLabel,
  emailAddressLabel,
  passwordLabel,
  requiredValidationMessage,
  emailValidationMessage,
  passwordTooShortMessage,
  submitLabel,
  formatEmailSuggestion,
  captchaReference,
  submitHandler,
  submitDisabled,
  CaptchaElement,
  serverValidation,
  companyNameLabel,
  websiteUrlLabel,
  chamberOfCommerceNumberLabel,
  countryOptions,
  languageOptions,
  urlValidationMessage,
}) => {
  const {
    handleSubmit, errors, control, setValue,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      captchaResponse: '',
      companyName: companyNameLabel && '',
      websiteUrl: websiteUrlLabel && '',
      chamberOfCommerceNumber: chamberOfCommerceNumberLabel && '',
      country: countryOptions && '',
      language: languageOptions && '',
    },
  });

  const onCaptchaVerify = useCallback(
    (token) => setValue('captchaResponse', token),
    [setValue],
  );

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <BasicSignupFields
        firstNameLabel={firstNameLabel}
        lastNameLabel={lastNameLabel}
        emailAddressLabel={emailAddressLabel}
        passwordLabel={passwordLabel}
        requiredValidationMessage={requiredValidationMessage}
        emailValidationMessage={emailValidationMessage}
        passwordTooShortMessage={passwordTooShortMessage}
        formatEmailSuggestion={formatEmailSuggestion}
        control={control}
        errors={errors}
      />

      { companyNameLabel && (
      <div className="form-item">
        <TextFieldWithController
          name="companyName"
          control={control}
          variant="filled"
          id="signup-buyer-company-name"
          label={companyNameLabel}
        />
        <ValidationError />
      </div>
      )}

      { websiteUrlLabel && (
        <div className="form-item">
          <TextFieldWithController
            name="websiteUrl"
            control={control}
            rules={getUrlValidation(
              urlValidationMessage,
              requiredValidationMessage,
            )}
            variant="filled"
            id="signup-buyer-website"
            label={websiteUrlLabel}
            onFocus={
              ({ target }) => {
                setValue(
                  'websiteUrl',
                  target.value === '' ? 'https://' : target.value,
                );
              }
            }
          />
          <ValidationError errors={errors} name="websiteUrl" />
        </div>
      )}

      { chamberOfCommerceNumberLabel && (
        <div className="form-item">
          <TextFieldWithController
            control={control}
            name="chamberOfCommerceNumber"
            rules={getRequiredValidation(requiredValidationMessage)}
            variant="filled"
            id="signup-buyer-chamber-of-commerce"
            label={chamberOfCommerceNumberLabel}
          />
          <ValidationError errors={errors} name="chamberOfCommerceNumber" />
        </div>
      )}

      { countryOptions && (
        <>
          <SpacingContainer />
          <div className="form-item">
            <SelectWithController
              name="country"
              control={control}
              variant="outlined"
              fullWidth
              label="Country"
              options={countryOptions}
            />
            <ValidationError />
          </div>
        </>
      )}

      { languageOptions && (
        <>
          <SpacingContainer />
          <div className="form-item">
            <SelectWithController
              name="language"
              control={control}
              variant="outlined"
              fullWidth
              label="Language"
              options={languageOptions}
            />
            <ValidationError />
          </div>
        </>
      )}

      <SpacingContainer />

      <CaptchaContainer>
        <div className="form-item">
          <Controller
            name="captchaResponse"
            control={control}
            rules={getRequiredValidation(requiredValidationMessage)}
            render={() => (
              <CaptchaElement
                ref={captchaReference}
                onVerify={onCaptchaVerify}
              />
            )}
          />

          <ValidationError errors={errors} name="captchaResponse" />
        </div>
      </CaptchaContainer>

      <SpacingContainer />
      { serverValidation }
      <Button
        fullWidth
        variant="contained"
        disabled={submitDisabled}
        className="account-register-submit"
        type="submit"
      >
        {submitLabel}
      </Button>
    </form>
  );
};

SignupForm.propTypes = {
  firstNameLabel: PropTypes.string.isRequired,
  lastNameLabel: PropTypes.string.isRequired,
  emailAddressLabel: PropTypes.string.isRequired,
  passwordLabel: PropTypes.string.isRequired,
  emailValidationMessage: PropTypes.string.isRequired,
  requiredValidationMessage: PropTypes.string.isRequired,
  passwordTooShortMessage: PropTypes.string.isRequired,
  submitLabel: PropTypes.string.isRequired,
  formatEmailSuggestion: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  serverValidation: PropTypes.node,
  submitDisabled: PropTypes.bool,
  CaptchaElement: PropTypes.elementType.isRequired,
  captchaReference: PropTypes.shape({}),
  companyNameLabel: PropTypes.string,
  websiteUrlLabel: PropTypes.string,
  chamberOfCommerceNumberLabel: PropTypes.string,
  countryOptions: PropTypes.arrayOf(PropTypes.shape({})),
  languageOptions: PropTypes.arrayOf(PropTypes.shape({})),
  urlValidationMessage: PropTypes.string,
};

SignupForm.defaultProps = {
  serverValidation: undefined,
  submitDisabled: false,
  captchaReference: undefined,
  companyNameLabel: undefined,
  websiteUrlLabel: undefined,
  chamberOfCommerceNumberLabel: undefined,
  countryOptions: undefined,
  languageOptions: undefined,
  urlValidationMessage: undefined,
};

export default SignupForm;
